import React, { useMemo } from 'react';
import { BackSide } from 'three';
import type { EnvironmentDef } from '../core/registries';

function hash01(n: number): number {
  const x = Math.sin(n * 12.9898) * 43758.5453;
  return x - Math.floor(x);
}

function CuteTree({
  position,
  scale = 1,
  name,
}: {
  position: [number, number, number];
  scale?: number;
  name: string;
}) {
  const green = hash01(position[0] * 3 + position[2]) > 0.5 ? '#3FA34D' : '#57CC5B';
  return (
    <group name={name} position={position} scale={scale}>
      <mesh position={[0, 0.55, 0]} castShadow>
        <cylinderGeometry args={[0.18, 0.24, 1.1, 8]} />
        <meshStandardMaterial color="#8B5A2B" roughness={0.85} />
      </mesh>
      <mesh position={[0, 1.45, 0]} castShadow>
        <sphereGeometry args={[0.75, 12, 12]} />
        <meshStandardMaterial color={green} roughness={0.7} />
      </mesh>
      <mesh position={[0.35, 1.7, 0.1]} castShadow>
        <sphereGeometry args={[0.4, 10, 10]} />
        <meshStandardMaterial color="#6FCF97" roughness={0.7} />
      </mesh>
    </group>
  );
}

function CuteHouse({
  position,
  yaw,
  wall,
  roof,
  name,
}: {
  position: [number, number, number];
  yaw: number;
  wall: string;
  roof: string;
  name: string;
}) {
  return (
    <group name={name} position={position} rotation={[0, yaw, 0]}>
      {/* No pale ground disc under the house — sits on shared grass plane */}
      <mesh position={[0, 1.0, 0]} castShadow>
        <boxGeometry args={[2.2, 2.0, 1.8]} />
        <meshStandardMaterial color={wall} roughness={0.55} />
      </mesh>
      <mesh position={[0, 2.35, 0]} rotation={[0, Math.PI / 4, 0]} castShadow>
        <coneGeometry args={[1.7, 1.1, 4]} />
        <meshStandardMaterial color={roof} roughness={0.5} />
      </mesh>
      <mesh position={[0.55, 1.1, 0.92]}>
        <boxGeometry args={[0.45, 0.45, 0.06]} />
        <meshStandardMaterial color="#7AD7FF" emissive="#7AD7FF" emissiveIntensity={0.35} />
      </mesh>
      <mesh position={[-0.55, 1.1, 0.92]}>
        <boxGeometry args={[0.45, 0.45, 0.06]} />
        <meshStandardMaterial color="#7AD7FF" emissive="#7AD7FF" emissiveIntensity={0.35} />
      </mesh>
      <mesh position={[0, 0.55, 0.92]}>
        <boxGeometry args={[0.4, 0.85, 0.06]} />
        <meshStandardMaterial color="#C77D3A" />
      </mesh>
    </group>
  );
}

function SoftCloud({ position, scale }: { position: [number, number, number]; scale: number }) {
  return (
    <group position={position} scale={scale}>
      <mesh position={[0, 0, 0]}>
        <sphereGeometry args={[1.1, 10, 10]} />
        <meshStandardMaterial color="#FFFFFF" transparent opacity={0.92} roughness={1} />
      </mesh>
      <mesh position={[1.0, 0.1, 0.2]}>
        <sphereGeometry args={[0.85, 10, 10]} />
        <meshStandardMaterial color="#FFFFFF" transparent opacity={0.9} roughness={1} />
      </mesh>
      <mesh position={[-0.9, 0.05, -0.15]}>
        <sphereGeometry args={[0.75, 10, 10]} />
        <meshStandardMaterial color="#FFFFFF" transparent opacity={0.9} roughness={1} />
      </mesh>
    </group>
  );
}

// Stronger wall colours so houses don't look washed out against the sky
const HOUSE_WALLS = ['#FFD9A0', '#FFB7C8', '#A8D4F5', '#C8F0A0', '#E0C4FF'];
const HOUSE_ROOFS = ['#D65A3A', '#2E6A96', '#E0A84A', '#5A9A78', '#D4553A'];

export interface EnvironmentDecorProps {
  env: EnvironmentDef;
  environmentId: string;
  routePoints: [number, number][];
  night?: boolean;
}

/**
 * Soft town dressing around the route — trees, toy houses, clouds —
 * so the scene reads as a kid place, not an empty gray plane.
 */
export const EnvironmentDecor: React.FC<EnvironmentDecorProps> = ({
  env,
  environmentId,
  routePoints,
  night = false,
}) => {
  const props = useMemo(() => {
    const trees: { pos: [number, number, number]; scale: number; key: string }[] = [];
    const houses: {
      pos: [number, number, number];
      yaw: number;
      wall: string;
      roof: string;
      key: string;
    }[] = [];

    routePoints.forEach((p, i) => {
      if (i === 0) return;
      const prev = routePoints[i - 1];
      const midX = (prev[0] + p[0]) / 2;
      const midZ = (prev[1] + p[1]) / 2;
      const yaw = Math.atan2(p[1] - prev[1], p[0] - prev[0]);
      const nx = Math.cos(yaw + Math.PI / 2);
      const nz = Math.sin(yaw + Math.PI / 2);

      // Trees on both sides
      [-1, 1].forEach((side, si) => {
        const dist = 6.5 + hash01(i * 10 + si) * 2.5;
        trees.push({
          pos: [midX + nx * dist * side, 0, midZ + nz * dist * side],
          scale: 0.85 + hash01(i * 7 + si) * 0.45,
          key: `tree-${i}-${si}`,
        });
        // Extra row further out
        if (i % 2 === 0) {
          trees.push({
            pos: [
              midX + nx * (dist + 3.2) * side + (hash01(i + si) - 0.5) * 2,
              0,
              midZ + nz * (dist + 3.2) * side,
            ],
            scale: 0.7 + hash01(i * 3 + si) * 0.35,
            key: `tree-far-${i}-${si}`,
          });
        }
      });

      // Houses a bit further out, alternating sides
      if (i % 2 === 1) {
        const side = i % 4 === 1 ? 1 : -1;
        const dist = 11 + hash01(i) * 2;
        houses.push({
          pos: [midX + nx * dist * side, 0, midZ + nz * dist * side],
          yaw: yaw + (side > 0 ? Math.PI : 0),
          wall: HOUSE_WALLS[i % HOUSE_WALLS.length],
          roof: HOUSE_ROOFS[i % HOUSE_ROOFS.length],
          key: `house-${i}`,
        });
      }
    });

    return { trees, houses };
  }, [routePoints]);

  const clouds = useMemo(
    () =>
      [
        [-8, 10, -12],
        [6, 11, 8],
        [-14, 12, 6],
        [16, 10, -4],
        [0, 13, 14],
      ] as [number, number, number][],
    []
  );

  // Tiny roadside flowers (stem + petals) — not loose spheres that look like litter
  const flowers = useMemo(() => {
    const out: { pos: [number, number, number]; color: string; key: string }[] = [];
    const palette = ['#FF6B6B', '#FFE66D', '#FF8FAB', '#FF9F1C'];
    for (let i = 0; i < 10; i += 1) {
      const a = (i / 10) * Math.PI * 2;
      const r = 7.5 + (i % 2) * 2.5;
      out.push({
        pos: [Math.cos(a) * r + (hash01(i) - 0.5) * 4, 0, Math.sin(a) * r + (hash01(i + 3) - 0.5) * 4],
        color: palette[i % palette.length],
        key: `flower-${i}`,
      });
    }
    return out;
  }, []);

  return (
    <group name={`env-decor-${environmentId}`}>
      {/* Soft sky wash via large backdrop sphere (inside-facing) */}
      <mesh name="sky-dome" scale={[-1, 1, 1]}>
        <sphereGeometry args={[90, 24, 16]} />
        <meshBasicMaterial color={env.sky} side={BackSide} />
      </mesh>

      {props.trees.map((t) => (
        <CuteTree key={t.key} name={t.key} position={t.pos} scale={t.scale} />
      ))}
      {props.houses.map((h) => (
        <CuteHouse
          key={h.key}
          name={h.key}
          position={h.pos}
          yaw={h.yaw}
          wall={h.wall}
          roof={h.roof}
        />
      ))}

      {!night
        ? clouds.map((c, i) => <SoftCloud key={`cloud-${i}`} position={c} scale={1.2 + (i % 3) * 0.25} />)
        : null}

      {!night
        ? flowers.map((f) => (
            <group key={f.key} name={f.key} position={f.pos}>
              <mesh position={[0, 0.18, 0]}>
                <cylinderGeometry args={[0.03, 0.04, 0.35, 6]} />
                <meshStandardMaterial color="#4CAF50" />
              </mesh>
              <mesh position={[0, 0.4, 0]}>
                <sphereGeometry args={[0.1, 8, 8]} />
                <meshStandardMaterial color={f.color} />
              </mesh>
              {(
                [
                  [0.1, 0.38, 0],
                  [-0.1, 0.38, 0],
                  [0, 0.38, 0.1],
                  [0, 0.38, -0.1],
                ] as [number, number, number][]
              ).map((p, pi) => (
                <mesh key={pi} position={p}>
                  <sphereGeometry args={[0.07, 6, 6]} />
                  <meshStandardMaterial color={f.color} />
                </mesh>
              ))}
            </group>
          ))
        : null}
    </group>
  );
};

export default EnvironmentDecor;
